<i18n>
  {
    "en": {
      "user": "User",
      "subdomain": "Subdomain",
      "password": "Password",
      "requiredPassword": "Password is required",
      "requiredUser": "User is required",
      "requiredSubdomain": "Subdomain is required",
      "language": "Language",
      "passwordSuccess": "The new password has been sent to the user's email address.",
      "noUser": "The user does not exist.",
      "noEmail": "The user does not have an email address configured. Contact the administrator.",
      "passError": "The password could not be regenerated. Please verify that the subdomain is correct.",
      "confirmTitle": "Confirm password reset?",
      "confirmDescription": "The new password will be sent to the email address of the user {0} for the {1} subdomain.",
      "resetLoading": "The new password is being generated. Please wait a moment.",
      "confirm": "Confirm"
    },
    "es": {
      "user": "Usuario",
      "subdomain": "Subdominio",
      "password": "Contraseña",
      "requiredPassword": "La contraseña es necesaria",
      "requiredUser": "El usuario es necesario",
      "requiredSubdomain": "El subdominio es necesario",
      "language":  "Idioma",
      "passwordSuccess":  "Se ha enviado la nueva contraseña al correo del usuario.",
      "noUser":  "El usuario no existe.",
      "noEmail":  "El usuario no tiene email configurado. Contacte con el administrador.",
      "passError":  "No se pudo regenerar la contraseña. Compruebe que el subdominio es correcto.",
      "confirmTitle": "¿Confirmar la regeneración de contraseña?",
      "confirmDescription": "Se enviará la nueva contraseña al correo del usuario {0} para el subdominio {1}.",
      "resetLoading": "Se está regenerando la nueva contraseña. Por favor espere un momento.",
      "confirm": "Confirmar"
    }
  }
</i18n>

<template>
  <v-main class="mygradient">
    <v-container class="fill-height" fluid>
      <v-row align="center" justify="center">
        <v-col cols="12" sm="8" md="4">
          <v-card class="elevation-12">
            <v-toolbar color="primary" dark flat>
              <div style="width: 100%" class="d-flex justify-space-between align-center">
                <img v-bind:src="require('@dynameat/assets/images/dynameat-logo-long.png')" alt="DynamEat name" contain height="30" />
                <div class="d-flex flex-shrink-1">
                  <button class="mr-2" v-on:click="() => changeLanguage('es')">
                    <v-img width="40" height="100%" :src="require('@dynameat/assets/images/es.png')" alt="Español" />
                  </button>
                  <button v-on:click="() => changeLanguage('en')">
                    <v-img width="40" height="100%" :src="require('@dynameat/assets/images/gb.png')" alt="English" />
                  </button>
                </div>
              </div>
            </v-toolbar>
            <v-card-text>
              <v-form @submit.prevent="login" id="login-form" v-model="validLoginForm">
                <v-text-field
                  :label="$t('user')"
                  name="login"
                  prepend-icon="fa-user"
                  type="text"
                  v-model="user"
                  :rules="[v => !!v ||  $t('requiredUser')]"
                  required
                ></v-text-field>
                <v-text-field
                  id="subdomain"
                  :label="$t('subdomain')"
                  name="subdomain"
                  prepend-icon="fa-network-wired"
                  type="text"
                  v-model="subdomain"
                  :rules="[v => !!v ||  $t('requiredSubdomain')]"
                  required
                ></v-text-field>
                <v-text-field
                  id="password"
                  :label="$t('password')"
                  name="password"
                  prepend-icon="fa-lock"
                  type="password"
                  v-model="password"
                  :rules="[v => !!v ||  $t('requiredPassword')]"
                  required
                ></v-text-field>
              </v-form>
            </v-card-text>
            <div style="width: 100%" class="d-flex ps-2 justify-space-between align-center">
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn class="mr-2" color="warning" :disabled="!user || !subdomain" @click="onToggleModal">
                  Reset password
                </v-btn>
              </v-card-actions>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn class="mr-2" color="primary" type="submit" form="login-form" :disabled="!validLoginForm">
                  Login
                </v-btn>
              </v-card-actions>
            </div>
          </v-card>
          <MadeWithLove class="pt-5"/>
        </v-col>
      </v-row>
      <PrettyAlert :text="textAlert" v-model="showAlert" :color="colorAlert"/>
      <dynameat-confirmation-modal
        :visible="confirmModalVisible"
        :title="isLoading ? $t('resetLoading') : $t('confirmTitle')"
        :description="this.$t('confirmDescription', [formatText(this.user), formatText(this.subdomain)])"
        :confirm-text="$t('confirm')"
        :is-loading="isLoading"
        @confirm="resetPassword"
        @cancel="onToggleModal"/>
      <dynameat-confirmation-modal :visible="loadingModalVisible"
                                 :is-loading="isLoading"
                                 :title="$t('resetLoading')"/>
    </v-container>
  </v-main>
</template>

<script>

import { APPLICATION_NAME } from '@dynameat/constants';
import i18n from '@core/plugins/i18n';
import PrettyAlert from './components/PrettyAlert.vue';
import MadeWithLove from './components/MadeWithLove.vue';
import DynameatConfirmationModal from '@dynameat/components/DynameatConfirmationModal';
import {mapActions, mapGetters} from 'vuex';

export default {
  components: {
    MadeWithLove,
    PrettyAlert,
    DynameatConfirmationModal
  },
  data() {
    return {
      user: null,
      subdomain: null,
      password: null,
      validLoginForm: false,
      showAlert: false,
      textAlert: '',
      colorAlert: 'info',
      language: { label: 'Español', value: 'es' },
      confirmModalVisible: false,
      loadingModalVisible: false
    };
  },
  computed: {
    name: () => APPLICATION_NAME,
    languages: () => [{ label: 'Español', value: 'es' }, { label: 'English', value: 'en' }],
    ...mapGetters(['isLoading']),
  },
  methods: {
    ...mapActions(['setLoading']),

    login() {
      const { user: username, subdomain, password } = this;
      this.$store.dispatch('login', { username, subdomain, password })
        .then(() => {
          const pushRoute = this.$route.query.redirect ? this.$route.query.redirect : '/';
          return this.$router.push(pushRoute);
        })
        .catch(() => this.showAlertAction('Error trying to login. Review your username, password and subdomain.', 'info'));
    },
    showAlertAction(text, color) {
      this.textAlert = text;
      this.showAlert = true;
      this.colorAlert = color;
    },
    async changeLanguage(language) {
      i18n.locale = language;
    },
    async resetPassword() {
      try {
        const { user: username, subdomain } = this;
        await this.setLoading(1);
        await this.$store.dispatch('regeneratePassword', { username, subdomain });
        this.showAlertAction(this.$t('passwordSuccess'), 'success');
      } catch (e) {
        const status = e?.response?.status;
        if (status === 409) {
          this.showAlertAction(this.$t('noUser'), 'error');
        } else if (status === 422) {
          this.showAlertAction(this.$t('noEmail'), 'error');
        } else {
          this.showAlertAction(this.$t('passError'), 'error');
        }
      } finally {
        await this.setLoading(-1);
        this.onToggleModal();
      }
    },
    onToggleModal() {
      this.confirmModalVisible = !this.confirmModalVisible;
    },
    formatText(text) {
      return '<span class="bold-text">' + text + '</span>'
    }
  }
};
</script>

<style scoped>
  #login {
    font-family: 'Avenir', Helvetica, Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-align: center;
    color: #2c3e50;
  }

  .mygradient {
    background: linear-gradient(0deg, rgba(255, 255, 255, 1) 0%, var(--v-primary-lighten3) 100%);
  }

  .select-language {
    padding-left: 10px;
    width: 100%;
    max-width: 125px;
  }


</style>
